import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import RelatedJournalEntries from "@/components/accounting/RelatedJournalEntries";
import StatusBadge, { type StatusTone } from "@/components/StatusBadge";
import { SOURCE_TYPE_ADJUSTMENT } from "@/constants/accounting";
import { useAdjustment } from "@/hooks/useAdjustments";
import { type Adjustment, APPROVAL_STATUS_LABEL, ApprovalStatus } from "@/interfaces/adjustments";
import { formatDateTime } from "@/utils/date-format";
import { formatAmount } from "@/utils/number-format";

/**
 * One adjustment, in full — the audit view.
 *
 * The decisions are why this exists. The blotter can say "Approved", but an approval with nobody's
 * name against it is not an audit trail, and a rejection whose reason cannot be read teaches the
 * maker nothing. Both live here, alongside the note, which is too long for a table cell.
 */

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", minWidth: 130, pt: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, flex: 1 }}>{children}</Box>
    </Box>
  );
}

/** The queue state each approval status maps to. */
const STATUS_TONE: Record<number, StatusTone> = {
  [ApprovalStatus.pending]: "warning",
  [ApprovalStatus.approved]: "success",
  [ApprovalStatus.rejected]: "error",
  [ApprovalStatus.cancelled]: "muted",
};

const DRAW_ORDER_LABEL: Record<string, string> = {
  lifo: "Newest credits first (LIFO)",
  fifo: "Oldest credits first (FIFO)",
};

export default function AdjustmentDetailDialog({
  adjustment,
  onClose,
}: {
  adjustment: Adjustment | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useAdjustment(adjustment?.id ?? null);
  if (!adjustment) return null;

  const signed = Number(adjustment.amount);
  const removing = signed < 0;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth={adjustment.appliedAt ? "md" : "sm"}>
      <DialogTitle
        sx={{
          fontFamily: "var(--font-lato)",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {removing ? "Remove" : "Add"} {formatAmount(Math.abs(signed))} credits
        </Box>
        {adjustment.status != null ? (
          <StatusBadge
            size="md"
            label={APPROVAL_STATUS_LABEL[adjustment.status] ?? "—"}
            tone={STATUS_TONE[adjustment.status] ?? "neutral"}
          />
        ) : null}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.75, pt: 1, pb: 3 }}>
        {isLoading ? (
          <CircularProgress size={22} />
        ) : (
          <>
            <Row label="Client">
              {adjustment.organizationName ?? "—"}
              {adjustment.groupName && adjustment.groupName !== adjustment.organizationName
                ? ` › ${adjustment.groupName}`
                : ""}
            </Row>
            <Row label="Reason">{adjustment.reasonLabel ?? adjustment.reasonCode ?? "—"}</Row>
            {data?.drawOrder ? (
              <Row label="Take credits from">
                {DRAW_ORDER_LABEL[data.drawOrder] ?? data.drawOrder}
              </Row>
            ) : null}
            <Row label="Raised by">
              {adjustment.createdByName ?? "—"} · {formatDateTime(adjustment.createdAt)}
            </Row>
            {adjustment.appliedAt ? (
              <Row label="Applied">{formatDateTime(adjustment.appliedAt)}</Row>
            ) : null}
            {adjustment.note ? (
              // The maker's justification, in full — it is truncated to nothing in the table.
              <Row label="Note">
                <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                  {adjustment.note}
                </Typography>
              </Row>
            ) : null}

            <Divider sx={{ my: 0.5 }} />

            <Typography
              sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", opacity: 0.6 }}
            >
              Decisions
            </Typography>
            {(data?.decisions ?? []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                {adjustment.status === ApprovalStatus.pending
                  ? "Not yet reviewed."
                  : "No decision recorded."}
              </Typography>
            ) : (
              data?.decisions.map((d) => (
                <Box key={d.id} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {d.decision === 1 ? "Approved" : "Rejected"} by {d.approverName ?? "unknown"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                    {formatDateTime(d.decidedAt)}
                  </Typography>
                  {d.note ? (
                    <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap", mt: 0.25 }}>
                      {d.note}
                    </Typography>
                  ) : null}
                </Box>
              ))
            )}

            {/* What the approval actually posted. Only once applied: nothing exists before that,
                and a rejected adjustment posts nothing at all. The component gates itself to GFG
                and follows the lot this adjustment minted, so a grant's mint entry comes too. */}
            {adjustment.appliedAt ? (
              <Box sx={{ mt: 1 }}>
                <RelatedJournalEntries
                  sourceType={SOURCE_TYPE_ADJUSTMENT}
                  sourceId={adjustment.id}
                />
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
